import type { Deal, DealStage, MainTab } from '@/types'
import { checkTransition } from '@/services/dealTransition.service'
import { getStageLabel } from './deals-constants'
import { shouldShowDealInReuniaoJourneyColumn } from './DealDetailPanel/reuniaoMeetingUtils'
import {
  getJourneyMacroIdForEtapa,
  getJourneyMacrosForTab,
  type JourneyMacroId,
  type JourneyMacroStage,
} from './dealsJourney.constants'

function isDroppableTransition(deal: Deal, etapa: DealStage, userRole?: string | null): boolean {
  const check = checkTransition(deal, etapa, userRole)
  return check.allowed || check.code === 'REQUIRED_FIELD_MISSING'
}

export function dealHasSavedProposalDraft(deal: Deal): boolean {
  return Boolean(deal.hasPropostaRascunho)
}

export function getDealJourneyStageLabel(deal: Deal): string {
  if (
    deal.etapa === 'proposta_elaboracao' &&
    shouldShowDealInReuniaoJourneyColumn(deal) &&
    dealHasSavedProposalDraft(deal)
  ) {
    return 'Proposta Pronta'
  }
  return getStageLabel(deal.etapa)
}

export function resolveJourneyMacroIdForDeal(deal: Deal): JourneyMacroId | null {
  if (shouldShowDealInReuniaoJourneyColumn(deal)) {
    return 'reuniao'
  }
  return getJourneyMacroIdForEtapa(deal.etapa)
}

export function groupDealsByJourneyMacro(
  deals: Deal[],
  mainTab: MainTab,
): Record<JourneyMacroId, Deal[]> {
  const macros = getJourneyMacrosForTab(mainTab)
  const grouped = Object.fromEntries(macros.map((m) => [m.id, [] as Deal[]])) as Record<
    JourneyMacroId,
    Deal[]
  >

  for (const deal of deals) {
    const macroId = resolveJourneyMacroIdForDeal(deal)
    if (macroId && grouped[macroId]) {
      grouped[macroId].push(deal)
    }
  }

  for (const macro of macros) {
    grouped[macro.id].sort((a, b) => b.driftDays - a.driftDays || a.empresaNome.localeCompare(b.empresaNome))
  }

  return grouped
}

export function countDealsByJourneyMacro(
  deals: Deal[],
  mainTab: MainTab,
): Record<JourneyMacroId, number> {
  const grouped = groupDealsByJourneyMacro(deals, mainTab)
  return Object.fromEntries(
    Object.entries(grouped).map(([id, list]) => [id, list.length]),
  ) as Record<JourneyMacroId, number>
}

export function isJourneyMacroDropAllowed(
  deal: Deal,
  macro: JourneyMacroStage,
  userRole?: string | null,
): boolean {
  if (resolveJourneyMacroIdForDeal(deal) === macro.id) return false
  return macro.etapas.some((etapa) => isDroppableTransition(deal, etapa, userRole))
}

export function resolveJourneyDropStage(
  deal: Deal,
  macro: JourneyMacroStage,
  userRole?: string | null,
): DealStage | null {
  if (macro.etapas.length === 0) return null
  if (resolveJourneyMacroIdForDeal(deal) === macro.id) return deal.etapa
  for (const etapa of macro.etapas) {
    if (isDroppableTransition(deal, etapa, userRole)) return etapa
  }
  return macro.etapas[0]
}
